import { ApiVersions } from '@gonfalon/openapi/api-versions';
import { useSuspenseQuery } from '@tanstack/react-query';

import type { AggregationType, Granularity, TimeRangeOption, UsageTabKey } from '../../types';
import { usageTabConfig } from '../constants/usageTabConfig';
import { getEffectiveTo } from '../utils/dates';
import { resolveQueryFn } from '../utils/resolveQueryFn';
import { shouldShowProjection } from '../utils/shouldShowProjection';

import { useUsageTimestamps } from './useUsageTimestamps';

type UsageTabDataProps = {
  usageTab: UsageTabKey;
  timeRange?: TimeRangeOption;
  month?: string;
  today: Date;
  aggregationType: AggregationType;
  granularity: Granularity;
};

export function useSuspenseUsageData({
  usageTab,
  timeRange,
  month,
  today,
  aggregationType,
  granularity,
}: UsageTabDataProps) {
  const timestamps = useUsageTimestamps({ timeRange, today, month, granularity });
  const effectiveTo = getEffectiveTo(timestamps.to, today, aggregationType);
  const { queryFnName } = usageTabConfig[usageTab];
  const queryFn = resolveQueryFn(queryFnName);
  const queryOptions = queryFn({
    apiVersion: ApiVersions.Beta,
    query: {
      aggregationType,
      from: timestamps.from.toString(),
      to: effectiveTo.toString(),
      granularity,
    },
  });

  const query = useSuspenseQuery(queryOptions);

  const hasProjection = shouldShowProjection(
    usageTab,
    aggregationType,
    granularity,
    query.data.projectedSeries?.length ?? 0,
  );

  return {
    data: query.data,
    timestamps,
    // The single source of truth for whether the aggregate (total) chart draws the dashed
    // projection. The solid actual line runs through today (the projection anchors there), and the
    // dashed projected line lifts off from today. Dimension breakdown charts must NOT receive this —
    // they never draw a projection.
    hasProjection,
  };
}
